import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import AppConfig from '../../appConfig';
import { setAuthTokenGetter } from '../../actions/authToken';
import InitDataFetcher from './InitDataFetcher';

/**
 * Resolves Auth0 user to Portal userId (EmployeeId) and stores in localStorage.
 * Only rendered when Auth0 is enabled (inside Auth0Provider).
 */
const UserIdResolver = ({ children }) => {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        const audience = AppConfig.auth0.audience;
        setAuthTokenGetter(() =>
            audience
                ? getAccessTokenSilently({ authorizationParams: { audience } })
                : getAccessTokenSilently()
        );
        if (!isAuthenticated) {
            localStorage.removeItem('userId');
            setResolved(true);
            return;
        }

        const cached = localStorage.getItem('userId');
        if (cached) {
            setResolved(true);
            return;
        }

        let cancelled = false;

        const resolveUser = async () => {
            try {
                const audience = AppConfig.auth0.audience;
                const token = audience
                    ? await getAccessTokenSilently({ authorizationParams: { audience } })
                    : await getAccessTokenSilently();
                const baseUrl = AppConfig.env('url');
                const response = await axios.get(`${baseUrl}Auth/ResolveUserId`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled && response?.data?.userId) {
                    localStorage.setItem('userId', response.data.userId);
                }
            } catch (err) {
                if (!cancelled) {
                    console.warn('Could not resolve userId from Auth0:', err?.response?.data || err.message);
                }
            } finally {
                if (!cancelled) setResolved(true);
            }
        };

        resolveUser();
        return () => { cancelled = true; };
    }, [isAuthenticated, getAccessTokenSilently]);

    // Show loading until we've attempted resolution (or found cache)
    if (!resolved) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <p>Loading your profile...</p>
            </div>
        );
    }

    return (
        <>
            <InitDataFetcher />
            {children}
        </>
    );
};

export default UserIdResolver;
